import { useState, useEffect, useMemo } from "react"
import { Source, Layer } from "react-map-gl/mapbox"
import { circle } from "@turf/turf"
import { getClosestPublicStations } from "@/lib/explore/api/geo"
import type { ICommune, POI } from "@/types/explore"
import type { FeatureCollection, Feature, Polygon } from "geojson"

const STATION_PERIMETER_RADIUS_500M = 0.5
const STATION_PERIMETER_RADIUS_800M = 0.8

interface StationPerimeterLayerProps {
  commune?: ICommune
}

export const StationPerimeterLayer = ({ commune }: StationPerimeterLayerProps) => {
  const codeInsee = commune?.code
  const lat = commune?.centre?.coordinates?.[1]
  const lon = commune?.centre?.coordinates?.[0]

  const [stations, setStations] = useState<POI[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!codeInsee || lat === undefined || lon === undefined) return
    let cancelled = false
    setLoading(true)
    getClosestPublicStations(lat, lon, 5000)
      .then((data) => {
        if (!cancelled) setStations(data)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [codeInsee, lat, lon])

  const buildCircles = (radius: number): FeatureCollection<Polygon> => {
    if (!stations || stations.length === 0) {
      return { type: "FeatureCollection", features: [] }
    }

    const circleFeatures: Feature<Polygon>[] = stations
      .filter((station) => station.lat !== undefined && station.lon !== undefined)
      .map((station) => {
        const circleFeature = circle([station.lon!, station.lat!], radius, { steps: 64, units: "kilometers" })
        return {
          ...circleFeature,
          properties: {
            ...circleFeature.properties,
            stationName: station.tags?.name || "Station",
            stationId: station.id,
            transportType: station.transportType || "train",
            distance: station.distance,
          },
        } as Feature<Polygon>
      })

    return { type: "FeatureCollection", features: circleFeatures }
  }

  const geoJson500m = useMemo(() => buildCircles(STATION_PERIMETER_RADIUS_500M), [stations])
  const geoJson800m = useMemo(() => buildCircles(STATION_PERIMETER_RADIUS_800M), [stations])

  if (loading || !stations) return null

  return (
    <>
      <Source id="station-perimeter-800m" type="geojson" data={geoJson800m}>
        <Layer id="station-perimeter-800m-fill" type="fill" paint={{ "fill-color": "#1890FF", "fill-opacity": 0.08 }} />
        <Layer
          id="station-perimeter-800m-border"
          type="line"
          paint={{ "line-color": "#1890FF", "line-width": 1.5, "line-dasharray": [3, 3] }}
        />
      </Source>
      <Source id="station-perimeter-500m" type="geojson" data={geoJson500m}>
        <Layer id="station-perimeter-500m-fill" type="fill" paint={{ "fill-color": "#1890FF", "fill-opacity": 0.15 }} />
        <Layer
          id="station-perimeter-500m-border"
          type="line"
          paint={{ "line-color": "#1890FF", "line-width": 2, "line-dasharray": [2, 2] }}
        />
      </Source>
    </>
  )
}
